import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  ShieldCheck,
  Layers,
  Radio,
  BookOpen,
  FolderKanban,
  BarChart3,
  Sparkles,
  ArrowRight,
  Database,
  FileText,
  GitBranch,
  Boxes,
  Group,
  Target,
  Play,
  Activity,
  Zap,
  ChevronRight,
} from 'lucide-react';

/* ─── Animation variants ─── */
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] as const } },
};

/* ─── Product cards (main 3) ─── */
interface ProductCard {
  key: string;
  title: string;
  fullName: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accentIcon: React.ComponentType<{ className?: string }>;
  route?: string;
  comingSoon?: boolean;
  gradient: string;
  border: string;
  glow: string;
  iconBg: string;
  accent: string;
}

const products: ProductCard[] = [
  {
    key: 'sdq',
    title: 'SDQ',
    fullName: 'Structured Data Quality',
    description:
      'Run, validate, and assess data quality on structured data sources. Connect to databases, profile schemas, and enforce quality rules with AI-powered validation.',
    icon: ShieldCheck,
    accentIcon: Database,
    route: '/sdq',
    gradient: 'from-emerald-500/8 via-teal-500/5 to-cyan-500/8',
    border: 'border-emerald-400/40 hover:border-emerald-400/70',
    glow: 'hover:shadow-emerald-500/20',
    iconBg: 'bg-emerald-500/15',
    accent: 'text-emerald-500',
  },
  {
    key: 'udq',
    title: 'UDQ',
    fullName: 'Unstructured Data Quality',
    description:
      'Run, validate, and assess data quality on unstructured data. Analyze documents, PDFs, images, and free-text content for completeness, consistency, and accuracy.',
    icon: Layers,
    accentIcon: FileText,
    route: '/udq',
    gradient: 'from-purple-500/8 via-violet-500/5 to-indigo-500/8',
    border: 'border-purple-400/40 hover:border-purple-400/70',
    glow: 'hover:shadow-purple-500/20',
    iconBg: 'bg-purple-500/15',
    accent: 'text-purple-500',
  },
  {
    key: 'pdq',
    title: 'PDQ',
    fullName: 'Proactive Data Quality',
    description:
      'Run, validate, and assess data quality in data pipelines. Embed quality gates into your ETL/ELT workflows and manage your data proactively before issues propagate.',
    icon: Radio,
    accentIcon: GitBranch,
    route: '/pdq',
    gradient: 'from-orange-500/8 via-amber-500/5 to-yellow-500/8',
    border: 'border-orange-400/40 hover:border-orange-400/70',
    glow: 'hover:shadow-orange-500/20',
    iconBg: 'bg-orange-500/15',
    accent: 'text-orange-500',
  },
];

/* ─── Building-block tools ─── */
interface ToolCard {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  color: string;
}

const tools: ToolCard[] = [
  {
    title: 'Data Profiling',
    description: 'Connect, discover schemas, and auto-generate quality rules from your data',
    icon: Search,
    route: '/source-profiling',
    color: 'text-blue-500 bg-blue-500/10',
  },
  {
    title: 'DQ Rule Catalog',
    description: 'Browse, search, and manage all data quality rules across your organization',
    icon: BookOpen,
    route: '/rules',
    color: 'text-sky-500 bg-sky-500/10',
  },
  {
    title: 'DQ Projects',
    description: 'Organize validations into projects and groups for structured governance',
    icon: FolderKanban,
    route: '/projects',
    color: 'text-violet-500 bg-violet-500/10',
  },
  {
    title: 'Groups',
    description: 'View and manage rule groups — add, edit, or remove rules within validation groups',
    icon: Group,
    route: '/groups',
    color: 'text-teal-500 bg-teal-500/10',
  },
  {
    title: 'Insights & Reports',
    description: 'View trends, analytics, and reports across all data quality activities',
    icon: BarChart3,
    route: '/reports',
    color: 'text-rose-500 bg-rose-500/10',
  },
];

/* ─── WorkflowDiagram — compact, gradient color palette ─── */
const WorkflowDiagram: React.FC = () => {
  const boxW = 150;
  const boxH = 50;
  const boxY = 72;
  const gap = 30;
  const startX = 20;
  // Unified indigo palette — modern SaaS look (Linear/Stripe/Vercel)
  const boxes = [
    { label: 'Profile\nData',             gradId: 'g-step', from: '#6366F1', to: '#4F46E5' },
    { label: 'Set Quality\nExpectations', gradId: 'g-step', from: '#6366F1', to: '#4F46E5' },
    { label: 'Execute',                   gradId: 'g-step', from: '#6366F1', to: '#4F46E5' },
    { label: 'Monitor',                   gradId: 'g-step', from: '#6366F1', to: '#4F46E5' },
    { label: 'Act',                       gradId: 'g-step', from: '#6366F1', to: '#4F46E5' },
  ].map((b, i) => ({ ...b, x: startX + i * (boxW + gap) }));

  const catalogX = boxes[1].x + boxW / 2;
  const catalogY = 10;
  const feedbackY = boxY + boxH + 38;
  const actCenterX = boxes[4].x + boxW / 2;
  const setQualCenterX = boxes[1].x + boxW / 2;
  const edgeColor = '#5B8BA8';      // muted steel-blue
  const arrowColor = '#2B6B96';     // steel-blue arrowhead
  const loopColor = '#0891B2';      // cyan (distinct from forward flow)
  const totalW = startX + 5 * boxW + 4 * gap + startX;

  return (
    <svg
      viewBox={`0 0 ${totalW} 190`}
      className="w-full h-auto"
      style={{ maxHeight: 230 }}
    >
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={arrowColor} />
        </marker>
        <marker id="arrow-loop" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={loopColor} />
        </marker>
        <filter id="softShadow" x="-10%" y="-10%" width="120%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
        </filter>
      </defs>

      {/* DQ Catalog cylinder — original steel-blue */}
      <g transform={`translate(${catalogX - 26}, ${catalogY})`} filter="url(#softShadow)">
        <ellipse cx="26" cy="6" rx="22" ry="5" fill="#3A7CA5" />
        <rect x="4" y="6" width="44" height="24" fill="#2B6B96" />
        <ellipse cx="26" cy="30" rx="22" ry="5" fill="#1F5576" />
        <path d="M 4 6 L 4 30" stroke="#1F5576" strokeWidth="1" />
        <path d="M 48 6 L 48 30" stroke="#1F5576" strokeWidth="1" />
        <text x="26" y="19" textAnchor="middle" fill="white"
              fontSize="8" fontWeight="700">DQ</text>
        <text x="26" y="28" textAnchor="middle" fill="white"
              fontSize="8" fontWeight="700">Catalog</text>
      </g>

      {/* Connector catalog → Set Quality */}
      <line x1={catalogX} y1={catalogY + 38} x2={catalogX} y2={boxY - 2}
            stroke={edgeColor} strokeWidth="1.5" markerEnd="url(#arrow)" />

      {/* 5 step boxes — original steel-blue */}
      {boxes.map((b, i) => (
        <g key={i} filter="url(#softShadow)">
          <rect x={b.x} y={boxY} width={boxW} height={boxH}
                fill="#2B6B96" rx="6" />
          {b.label.split('\n').map((line, li, arr) => {
            const centerY = boxY + boxH / 2;
            const y = arr.length === 2 ? centerY + (li === 0 ? -3 : 10) : centerY + 4;
            return (
              <text key={li}
                    x={b.x + boxW / 2}
                    y={y}
                    textAnchor="middle"
                    fill="white"
                    fontSize="11"
                    fontWeight="700"
                    letterSpacing="0.3">
                {line}
              </text>
            );
          })}
        </g>
      ))}

      {/* Forward arrows between boxes */}
      {boxes.slice(0, -1).map((b, i) => {
        const nextX = boxes[i + 1].x;
        return (
          <line key={i}
                x1={b.x + boxW} y1={boxY + boxH / 2}
                x2={nextX - 2} y2={boxY + boxH / 2}
                stroke={edgeColor} strokeWidth="1.5"
                markerEnd="url(#arrow)" />
        );
      })}

      {/* Optimize feedback loop */}
      <line x1={actCenterX} y1={boxY + boxH}
            x2={actCenterX} y2={feedbackY}
            stroke={loopColor} strokeWidth="1.5" />
      <line x1={actCenterX} y1={feedbackY}
            x2={setQualCenterX} y2={feedbackY}
            stroke={loopColor} strokeWidth="1.5" />
      <line x1={setQualCenterX} y1={feedbackY}
            x2={setQualCenterX} y2={boxY + boxH + 3}
            stroke={loopColor} strokeWidth="1.5" markerEnd="url(#arrow-loop)" />
      {/* Optimize pill */}
      <g>
        <rect x={(actCenterX + setQualCenterX) / 2 - 34}
              y={feedbackY + 5}
              width="68" height="16"
              fill="white" stroke={loopColor} strokeWidth="1.3" rx="8" />
        <text x={(actCenterX + setQualCenterX) / 2}
              y={feedbackY + 16}
              textAnchor="middle" fill={loopColor}
              fontSize="9" fontWeight="700" letterSpacing="1">OPTIMIZE</text>
      </g>
    </svg>
  );
};

/* ─── LogoChip — official brand logos in white rounded badge ─── */
const LogoChip: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div
    className="w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center hover:scale-110 transition-transform cursor-default"
    title={alt}
  >
    <img
      src={src}
      alt={alt}
      className="w-6 h-6 object-contain"
      loading="lazy"
    />
  </div>
);

/* ─── Component ─── */
const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-7xl mx-auto space-y-10 pb-8">
      {/* ── Hero Section — compact ── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0c0c2d] via-[#131345] to-[#1a1a5c] px-6 py-5 sm:px-8 sm:py-6"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-cargo-accent/10 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative z-10 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
                <Sparkles className="w-3 h-3 text-yellow-400" />
                <span className="text-[10px] font-semibold text-white/80 tracking-wide uppercase">AI-Powered Accelerator</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-200 to-cyan-200 bg-clip-text text-transparent">
                Assure
              </span>
            </h1>
            <p className="mt-1.5 text-sm sm:text-base text-blue-100/80 max-w-xl leading-snug font-light">
              Enterprise data quality platform — validate, certify, and govern your data with AI.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-400/20 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-200/90">Structured</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-500/10 border border-purple-400/20 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
              <span className="text-xs text-purple-200/90">Unstructured</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-orange-500/10 border border-orange-400/20 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-xs text-orange-200/90">Pipelines</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── How It Works — light-theme flow diagram section ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-5">
          <GitBranch className="w-5 h-5 text-cargo-accent" />
          <h2 className="text-lg font-bold text-cargo-text">How It Works</h2>
          <div className="flex-1 h-px bg-cargo-border/60" />
          <p className="text-xs text-cargo-muted">End-to-end data quality lifecycle</p>
        </div>

        <div className="bg-white border border-cargo-border rounded-2xl p-4 shadow-sm">
          <WorkflowDiagram />
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-cargo-border rounded-xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-600 mb-3">Supported Platforms</div>
            <div className="flex items-center gap-3 flex-wrap">
              <LogoChip src="https://api.iconify.design/logos:aws.svg" alt="AWS" />
              <LogoChip src="https://api.iconify.design/logos:microsoft-azure.svg" alt="Azure" />
              <LogoChip src="https://api.iconify.design/simple-icons:databricks.svg?color=%23FF3621" alt="Databricks" />
              <LogoChip src="https://api.iconify.design/logos:snowflake-icon.svg" alt="Snowflake" />
              <LogoChip src="https://api.iconify.design/logos:google-cloud.svg" alt="GCP" />
            </div>
          </div>
          <div className="bg-white border border-cargo-border rounded-xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-purple-600 mb-3">Supported Data Pipelines</div>
            <div className="flex items-center gap-3 flex-wrap">
              <LogoChip src="https://api.iconify.design/simple-icons:databricks.svg?color=%23FF3621" alt="Databricks" />
              <LogoChip src="https://api.iconify.design/logos:apache-spark.svg" alt="Spark" />
              <LogoChip src="https://api.iconify.design/logos:python.svg" alt="Python" />
              <LogoChip src="https://api.iconify.design/logos:dbt-icon.svg" alt="dbt" />
              <LogoChip src="https://api.iconify.design/logos:postgresql.svg" alt="SQL" />
              <LogoChip src="https://api.iconify.design/logos:airflow-icon.svg" alt="Airflow" />
            </div>
          </div>
          <div className="bg-white border border-cargo-border rounded-xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-orange-600 mb-3">Data Types</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full">
                <Database className="w-3 h-3 text-emerald-600" />
                <span className="text-[11px] font-semibold text-emerald-700">Structured</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 border border-purple-200 rounded-full">
                <FileText className="w-3 h-3 text-purple-600" />
                <span className="text-[11px] font-semibold text-purple-700">Unstructured</span>
              </div>
            </div>
            <div className="text-[11px] text-cargo-muted mt-2">PDFs · Docs · Images · Free text</div>
          </div>
        </div>
      </motion.div>

      {/* ── Building Blocks Section ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-5">
          <Boxes className="w-5 h-5 text-cargo-accent" />
          <h2 className="text-lg font-bold text-cargo-text">Building Blocks</h2>
          <div className="flex-1 h-px bg-cargo-border/60" />
          <p className="text-xs text-cargo-muted">Core components that power your data validation workflows</p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          {tools.map((t) => {
            const Icon = t.icon;
            return (
              <motion.button
                key={t.title}
                variants={scaleIn}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(t.route)}
                className="card-glow group text-left bg-white border border-cargo-border/60 hover:border-cargo-accent/40
                           rounded-xl p-5 transition-all duration-300 cursor-pointer"
              >
                <div className={`w-10 h-10 rounded-lg ${t.color} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-cargo-text mb-1 group-hover:text-cargo-accent transition-colors">
                  {t.title}
                </h3>
                <p className="text-xs text-cargo-muted leading-relaxed">{t.description}</p>
              </motion.button>
            );
          })}
        </motion.div>
      </motion.div>

      {/* ── Modules ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-5">
          <Boxes className="w-5 h-5 text-cargo-accent" />
          <h2 className="text-lg font-bold text-cargo-text">Modules</h2>
          <div className="flex-1 h-px bg-cargo-border/60" />
          <p className="text-xs text-cargo-muted">Data quality assessment engines for every data type</p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-3 gap-5"
        >
          {products.map((p) => {
            const Icon = p.icon;
            const AccentIcon = p.accentIcon;
            return (
              <motion.button
                key={p.key}
                variants={fadeUp}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                onClick={() => !p.comingSoon && p.route && navigate(p.route)}
                disabled={p.comingSoon}
                className={`card-glow group relative text-left bg-gradient-to-br ${p.gradient}
                           border ${p.border} rounded-2xl p-6 sm:p-7 transition-all duration-300
                           hover:shadow-xl ${p.glow}
                           ${p.comingSoon ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {/* Coming soon badge */}
                {p.comingSoon && (
                  <div className="absolute top-4 right-4 px-2.5 py-1 bg-orange-500/15 text-orange-500 text-[10px] font-bold uppercase tracking-wider rounded-full border border-orange-500/20">
                    Coming Soon
                  </div>
                )}

                {/* Background accent icon */}
                <AccentIcon className={`absolute bottom-4 right-4 w-20 h-20 ${p.accent} opacity-[0.04] transition-opacity duration-300 group-hover:opacity-[0.08]`} />

                <div className="relative z-10">
                  <div className={`w-12 h-12 rounded-xl ${p.iconBg} flex items-center justify-center mb-5`}>
                    <Icon className={`w-6 h-6 ${p.accent}`} />
                  </div>

                  <h2 className="text-2xl font-bold text-cargo-text tracking-tight">{p.title}</h2>
                  <p className={`text-sm font-medium ${p.accent} mt-0.5 mb-3`}>{p.fullName}</p>
                  <p className="text-sm text-cargo-muted leading-relaxed">{p.description}</p>

                  {!p.comingSoon && (
                    <div className={`inline-flex items-center gap-1.5 mt-5 text-sm font-semibold ${p.accent} group-hover:gap-2.5 transition-all`}>
                      Get Started
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  )}
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Home;
